import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { useInView } from '@/hooks/useInView';

/**
 * A photograph that arrives rather than appears.
 *
 * Two problems, one component. A decoded image swapping in over a grey box is a visible flicker on
 * every card in a grid, and a broken URL leaves a torn-page icon in the middle of a carefully made
 * layout. So the image fades up over its own placeholder, and a failure falls back to the
 * placeholder rather than to the browser's default.
 *
 * The fade is on the image, not the container, so the placeholder underneath stays put and the box
 * never changes size — which is the whole point of reserving the aspect ratio in the first place.
 */
export function Photo({
  src,
  alt,
  className,
  imageClassName,
  fallback,
  eager = false,
  priority = false,
}: {
  src?: string | null;
  alt: string;
  className?: string;
  imageClassName?: string;
  /** Drawn under the photograph, and left showing if there is no photograph or it fails. */
  fallback?: React.ReactNode;
  eager?: boolean;
  priority?: boolean;
}) {
  const [state, setState] = useState<'loading' | 'loaded' | 'failed'>('loading');
  const image = useRef<HTMLImageElement>(null);

  useEffect(() => {
    setState('loading');
  }, [src]);

  useEffect(() => {
    // An image already in the browser cache can finish before React attaches its onLoad, which
    // would leave it permanently at zero opacity. `complete` is how you find out that happened.
    if (image.current?.complete && image.current.naturalWidth > 0) {
      setState('loaded');
    }
  }, [src]);

  return (
    <div className={cn('relative overflow-hidden bg-sunken', className)}>
      {(state !== 'loaded' || !src) && (
        <div className="absolute inset-0 flex items-center justify-center">{fallback}</div>
      )}

      {src && state !== 'failed' && (
        <img
          ref={image}
          src={src}
          alt={alt}
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
          fetchPriority={priority ? 'high' : 'auto'}
          onLoad={() => setState('loaded')}
          onError={() => setState('failed')}
          className={cn(
            'size-full object-cover transition-opacity duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]',
            state === 'loaded' ? 'opacity-100' : 'opacity-0',
            imageClassName,
          )}
        />
      )}
    </div>
  );
}

/** A section that rises into place the first time it is scrolled to. */
export function Reveal({
  children,
  className,
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const { ref, seen } = useInView<HTMLDivElement>();

  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={cn(
        'transition-[opacity,transform] duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]',
        // No `translate-y-0` once seen: any transform, even a zero one, makes this element the
        // containing block for its `position: fixed` descendants. Dropping the class entirely
        // leaves no transform at all.
        seen ? 'opacity-100' : 'translate-y-4 opacity-0',
        className,
      )}
    >
      {children}
    </div>
  );
}
